"use client";

import { User, Mail, Phone } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { FormLabels } from "../types";
import { isValidEmail } from "../lib/email";

interface Data {
  first_name: string;
  email: string;
  phone: string;
}

interface Props {
  labels: FormLabels;
  data: Data;
  onChange: (d: Data) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
}

function hasPhone(v: string): boolean {
  return v.replace(/[^\d+]/g, "").length >= 8;
}

export default function Step1Contact({ labels, data, onChange, onSubmit, submitting }: Props) {
  const valid =
    data.first_name.trim().length > 0 && isValidEmail(data.email) && hasPhone(data.phone);

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-1">
        {labels.step_1_title}
      </h2>
      <p className="text-sm text-stone-700 mb-5">30 seconden invullen — Gelijk digitaal via mail of morgen op de mat.</p>

      <IconField
        label={labels.first_name_label}
        icon={User}
        name="first_name"
        type="text"
        value={data.first_name}
        autoComplete="given-name"
        onChange={(v) => onChange({ ...data, first_name: v })}
        required
      />

      <IconField
        label={labels.email_label}
        icon={Mail}
        name="email"
        type="email"
        value={data.email}
        autoComplete="email"
        inputMode="email"
        onChange={(v) => onChange({ ...data, email: v })}
        required
      />

      <IconField
        label={labels.phone_label}
        icon={Phone}
        name="phone"
        type="tel"
        value={data.phone}
        autoComplete="tel"
        inputMode="tel"
        placeholder="06 1234 5678"
        onChange={(v) => onChange({ ...data, phone: v })}
        required
      />

      {/* Honeypot — visually hidden but present in the DOM. Bots fill it in. */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
        <label>
          Laat dit veld leeg
          <input type="text" name="honeypot" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <button
        type="submit"
        disabled={!valid || submitting}
        className="w-full rounded-pill bg-terracotta text-white font-semibold py-4 text-base shadow-card hover:bg-terracotta-700 hover:shadow-card-hover disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-terracotta disabled:hover:shadow-card transition-all mt-2 focus:outline-none focus-visible:shadow-focus"
      >
        {submitting ? "Verzenden…" : labels.submit_step_1}
      </button>

      <p className="text-xs text-stone-500 text-center pt-1">
        Geen verplichtingen. We bellen alleen als u dat zelf wilt.
      </p>
    </form>
  );
}

/** Input with a left-side lucide icon, large 52px height for the 55+ audience. */
function IconField({
  label,
  icon: Icon,
  name,
  value,
  type,
  autoComplete,
  inputMode,
  placeholder,
  required,
  onChange,
}: {
  label: string;
  icon: LucideIcon;
  name: string;
  value: string;
  type: string;
  autoComplete: string;
  inputMode?: "email" | "tel" | "text" | "numeric" | "search" | "url" | "none" | "decimal";
  placeholder?: string;
  required?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink block mb-1.5">{label}</span>
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-stone-500"
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <input
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
          {...(inputMode ? { inputMode } : {})}
          {...(placeholder ? { placeholder } : {})}
          className="h-[52px] w-full rounded-card border-2 border-stone-100 bg-white pl-11 pr-4 text-base text-ink placeholder:text-stone-300 transition-colors focus:border-terracotta focus:outline-none"
        />
      </div>
    </label>
  );
}
